<template>
  <NuxtLayout name="default">
    <ClientOnly>
      <MoneyBackCard />
      <WinEverCard />
    </ClientOnly>

    <NuxtLayout name="teasers" :no-results="!loading && teasers.length <= 0" @refresh="refetch">
      <Component
        :is="component"
        v-for="({ component, id, props }, index) in teasers.slice(
          0,
          trustTeaserWinnersCarouselStart,
        )"
        :key="id"
        v-bind="props"
        :fetchpriority="index < priorityFetchAmount ? 'high' : 'low'"
        @click="handleTeaserClick(id)"
      />

      <TrustTeaserCarousel class="carousel" :slides="trustTeaserWinners">
        <template #headline>
          <h2 class="headline-h5-headline">
            {{ trustTeaserCarousels?.winnersTitle }}
          </h2>
        </template>
      </TrustTeaserCarousel>

      <Component
        :is="component"
        v-for="({ component, id, props }, index) in teasers.slice(
          trustTeaserWinnersCarouselStart,
          trustTeaserAppReviewsStart,
        )"
        :key="id"
        v-bind="props"
        :fetchpriority="index < priorityFetchAmount ? 'high' : 'low'"
        @click="handleTeaserClick(id)"
      />

      <TrustTeaserCarousel class="carousel" :slides="trustTeaserAppReviews">
        <template #headline>
          <h2 class="headline-h5-headline">
            {{ trustTeaserCarousels?.appReviewsTitle }}
          </h2>
        </template>
      </TrustTeaserCarousel>

      <Component
        :is="component"
        v-for="({ component, id, props }, index) in teasers.slice(trustTeaserAppReviewsStart)"
        :key="id"
        v-bind="props"
        :fetchpriority="index < priorityFetchAmount ? 'high' : 'low'"
        @click="handleTeaserClick(id)"
      />
    </NuxtLayout>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import type { TeaserPayload } from '#api/shared/@types/generated/backend/graphql-schema-types'
import type { TrustTeaserCardAppReviewProps } from '~/components/TrustTeaser/TrustTeaserCardAppReview.vue'
import type { TrustTeaserCardWinnerProps } from '~/components/TrustTeaser/TrustTeaserCardWinner.vue'

import { FREE_TO_PLAY_ID, FREE_TO_PLAY_ITEM_NAME } from '#free-to-play/app/constants/constants'

definePageMeta({
  footer: 'full',
  header: 'full',
  layout: false,
  middleware: ['age-verification', 'self-excluded'],
  tracking: { name: 'Homepage' },
})

const { isDesktop } = useDevice()
const licensedTerritory = useLicensedTerritory({ raw: true })

const { loading, refetch, result } = useTeasersQuery(
  { licensedTerritory },
  {
    nextFetchPolicy: 'cache-and-network',
    pollInterval: 3_000_000,
  },
)

const { data: trustTeaserCarouselsData } = await useContent().getTrustTeaserCarousels()

const { filterFreeToPlayTeasers } = useFreeToPlay()

const {
  feature: { f2pLoyalty: isF2pLoyaltyEnabled },
} = useRuntimeConfig().public

const auth = useAuth()
const isLoggedIn = ref(false)

onMounted(() => {
  if (!isF2pLoyaltyEnabled) return
  isLoggedIn.value = auth.isLoggedIn.value
})

const trustTeaserAppReviewsStart = computed(() => (isLoggedIn.value ? 6 : 4))
const trustTeaserWinnersCarouselStart = computed(() => (isLoggedIn.value ? 4 : 2))

const priorityFetchAmount = computed(() => (isDesktop ? 6 : 3))

const teasers = computed(() => {
  const filteredTeasers = filterFreeToPlayTeasers(result.value?.teasers.teasers as TeaserPayload[])

  return mapToTeaserComponents(filteredTeasers, licensedTerritory, refetch)
})

const trustTeaserAppReviews = computed(() => {
  const items = trustTeaserCarousels.value?.appReviewsSlidesCollection?.items ?? []

  // TODO Remove fallbacks when contentful schema finally gets it’s shit together
  return items.filter(isTruthy).map<TrustTeaserCardAppReviewProps>((item) => ({
    date: item.date,
    rating: item.rating ?? 5,
    review: item.review ?? '',
    store: item.store ?? '',
    username: item.username ?? '',
  }))
})

const trustTeaserCarousels = computed(() => trustTeaserCarouselsData.value?.items?.[0] ?? null)

const trustTeaserWinners = computed(() => {
  const items = trustTeaserCarousels.value?.winnersSlidesCollection?.items ?? []

  // TODO Remove fallbacks when contentful schema finally gets it’s shit together
  return items.filter(isTruthy).map<Omit<TrustTeaserCardWinnerProps, 'index'>>((item) => ({
    game: item.game ?? '',
    image: {
      alt: item.image?.description ?? '',
      src: item.image?.url ?? '',
    },
    state: item.state?.id ?? '',
    winningAmount: item.winningAmount ?? 0,
  }))
})

const { pushSelectListItemEvent, pushViewListEvent } = useTracking()
const { createGenericListItem, createTeaserListItems } = useTrackingData()
const { logTrackingError } = useTrackingErrorLog()

const trackingTeaserListItems = ref<ReturnType<typeof createTeaserListItems>>([])

const handleTeaserClick = (id: string) => {
  if (!result.value) return

  const teaser = result.value.teasers.teasers.find((teaser) => {
    switch (teaser.__typename) {
      case 'FreeToPlayTeaserPayload':
        return FREE_TO_PLAY_ITEM_NAME === id
      case 'HighFrequencyTeaserPayload':
      case 'LotteryTeaserPayload':
        return teaser.id === id
      case 'ScratchcardTeaserPayload':
        return teaser.slug === id
      default:
        return false
    }
  })
  if (!teaser) return

  const teaserItem = trackingTeaserListItems.value.find((item) => {
    switch (teaser.__typename) {
      case 'FreeToPlayTeaserPayload':
        return item.item_id === FREE_TO_PLAY_ID
      case 'HighFrequencyTeaserPayload':
      case 'LotteryTeaserPayload':
        return item.item_id === teaser.id
      case 'ScratchcardTeaserPayload':
        return item.item_name === teaser.name
      default:
        return false
    }
  })
  if (!teaserItem) return

  try {
    pushSelectListItemEvent({
      items: [teaserItem],
      list_id: TRACKING_TEASERS_LIST_ID,
      list_name: TRACKING_TEASERS_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('select_list_item', err)
  }
}

const TRACKING_TEASERS_LIST_NAME = 'Homepage Items (Teasers)'
const TRACKING_TEASERS_LIST_ID = 'home_items_teasers_list'
let unwatchTeasers: Maybe<() => void> = null
unwatchTeasers = watchImmediate(result, (list) => {
  if (!list?.teasers || list.teasers.teasers.length === 0) return

  try {
    trackingTeaserListItems.value = createTeaserListItems(list.teasers.teasers)
    pushViewListEvent({
      items: trackingTeaserListItems.value,
      list_id: TRACKING_TEASERS_LIST_ID,
      list_name: TRACKING_TEASERS_LIST_NAME,
    })

    if (unwatchTeasers) unwatchTeasers()
  } catch (err) {
    logTrackingError('view_list', err)
  }
})

const TRACKING_WINNERS_LIST_NAME = 'Homepage Items (Winners)'
const TRACKING_WINNERS_LIST_ID = 'home_items_winners_list'
let unwatchWinners: Maybe<() => void> = null
unwatchWinners = watchImmediate(trustTeaserWinners, (list) => {
  if (!list || list.length === 0) return

  try {
    pushViewListEvent({
      items: createGenericListItem({
        itemCategory: TRACKING_WINNERS_LIST_ID,
        list: list.map((item) => ({
          ...item,
          name: item.game,
        })),
      }),
      list_id: TRACKING_WINNERS_LIST_ID,
      list_name: TRACKING_WINNERS_LIST_NAME,
    })

    if (unwatchWinners) unwatchWinners()
  } catch (err) {
    logTrackingError('view_list', err)
  }
})

const TRACKING_TRUST_LIST_NAME = 'Homepage Items (Trust)'
const TRACKING_TRUST_LIST_ID = 'home_items_trust_list'
let unwatchWTrustTeasers: Maybe<() => void> = null
unwatchWTrustTeasers = watchImmediate(trustTeaserAppReviews, (list) => {
  if (!list || list.length === 0) return

  try {
    pushViewListEvent({
      items: createGenericListItem({
        itemCategory: TRACKING_TRUST_LIST_ID,
        list: list.map((item) => ({ ...item, name: item.username })),
      }),
      list_id: TRACKING_TRUST_LIST_ID,
      list_name: TRACKING_TRUST_LIST_NAME,
    })

    if (unwatchWTrustTeasers) unwatchWTrustTeasers()
  } catch (err) {
    logTrackingError('view_list', err)
  }
})
</script>

<style lang="scss" scoped>
.carousel {
  margin: 0.75rem -0.5rem 1rem;

  @include media-desktop {
    grid-column: span 2;
    margin: -0.5rem -0.5rem 0.5rem;
  }
}
</style>
